import {
  SIGNAL_METHOD_UNITS,
  type CalibrationExclusionReason,
  type ChannelMetricValues,
  type DistributionMetrics,
  type ExcludedCalibrationTest,
  type MetricStats,
  type MetricsReport,
} from '@/services/api/classificationService';
import { formatTestStatus } from '@/lib/testStatus';

// ---------------------------------------------------------------------------
// Shared wording for tests and exclusions (also used by the Calibration tool)
// ---------------------------------------------------------------------------

export const EXCLUSION_REASON_LABELS: Record<CalibrationExclusionReason, string> = {
  not_found: 'not found',
  measurement_incomplete: 'measurements not finished',
  no_baseline_data: 'no baseline data',
  unreadable_data: 'impedance data could not be read',
};

/** A test's human name: its sample code, else when it was created, else the start of its ID. */
export function formatTestName(test: { testId: string; sampleCode?: string | null; createdAt?: string | null }): string {
  return test.sampleCode
    || (test.createdAt ? new Date(test.createdAt).toLocaleString() : test.testId.slice(0, 8));
}

export function describeExclusion(test: ExcludedCalibrationTest): string {
  const status = test.reason === 'measurement_incomplete' && test.status ? ` (${formatTestStatus(test.status)})` : '';
  return `${EXCLUSION_REASON_LABELS[test.reason] ?? test.reason}${status}`;
}

// ---------------------------------------------------------------------------
// Metrics shown in the report, in the order the Calibration tool shows them
// ---------------------------------------------------------------------------

type MetricKey = keyof DistributionMetrics & keyof ChannelMetricValues;

const REPORT_METRICS: { key: MetricKey; label: string; unit: string }[] = [
  { key: 'curveRadius', label: 'Curve Radius', unit: 'Ω' },
  { key: 'maxImaginary', label: 'Max Imaginary', unit: 'Ω' },
  { key: 'rsAt10kHz', label: 'Rs @ 10kHz', unit: 'Ω' },
  { key: 'rsDrift', label: 'Rs Drift', unit: 'Ω' },
  { key: 'deltaSignal', label: 'ΔMax Imaginary', unit: SIGNAL_METHOD_UNITS.MaxImaginary },
  { key: 'deltaNyquistArea', label: 'ΔNyquist Area', unit: SIGNAL_METHOD_UNITS.NyquistArea },
  { key: 'deltaFreqArea', label: 'ΔFrequency Area', unit: SIGNAL_METHOD_UNITS.FreqArea },
  { key: 'deltaAscendingArcArea', label: 'ΔAscending Arc', unit: SIGNAL_METHOD_UNITS.AscendingArcArea },
  { key: 'deltaMaxImaginaryInterpolated', label: 'ΔMax Imaginary Interpolated', unit: SIGNAL_METHOD_UNITS.MaxImaginaryInterpolated },
  { key: 'deltaMaxImaginaryAbsolute', label: 'ΔMax Imaginary Absolute', unit: SIGNAL_METHOD_UNITS.MaxImaginaryAbsolute },
];

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Two decimals; scientific notation for very large magnitudes; an em dash when there is no value. */
export function formatMetricValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const fixed = Math.abs(value) >= 1e6 ? value.toExponential(3) : value.toFixed(2);
  return fixed.startsWith('-') && Number(fixed) === 0 ? fixed.slice(1) : fixed;
}

function count(stats: MetricStats): number {
  return stats.rawValues?.length ?? 0;
}

/** Coefficient of variation in percent; undefined for fewer than two values or a zero mean. */
function coefficientOfVariation(stats: MetricStats): number | null {
  return count(stats) >= 2 && stats.mean !== 0 ? (stats.stdDev / Math.abs(stats.mean)) * 100 : null;
}

function summaryCell(stats: MetricStats): string {
  const n = count(stats);
  if (n === 0) return '<td class="num muted">—</td>';
  return `<td class="num">${formatMetricValue(stats.mean)} ± ${formatMetricValue(stats.stdDev)} <span class="muted">(n=${n})</span></td>`;
}

function metricHeader(metric: { label: string; unit: string }): string {
  return `${escapeHtml(metric.label)} <span class="unit">(${escapeHtml(metric.unit)})</span>`;
}

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

export interface MetricsReportContext {
  /** Name of the person who generated the report */
  generatedBy: string;
}

/**
 * Builds a self-contained HTML page (inline CSS, no external files) listing every calibration
 * metric for every channel: a summary grid, per-channel statistics and each test's values.
 * Metrics only — no threshold recommendations.
 */
export function buildMetricsReportHtml(report: MetricsReport, context: MetricsReportContext): string {
  const generatedAt = new Date(report.analysisTimestamp);
  const channelNames = report.channels.map((c) => `CH${c.channelId}`);

  const summary = report.channels.length === 0 ? '' : `
<section>
  <h2>Summary by channel</h2>
  <p class="hint">Mean ± standard deviation; n is the number of tests with a value for that metric on that channel.</p>
  <div class="scroll"><table>
    <thead><tr><th>Metric</th>${channelNames.map((ch) => `<th class="num">${ch}</th>`).join('')}</tr></thead>
    <tbody>
${REPORT_METRICS.map((m) => `      <tr><th scope="row">${metricHeader(m)}</th>${report.channels.map((c) => summaryCell(c.distributions[m.key])).join('')}</tr>`).join('\n')}
    </tbody>
  </table></div>
</section>`;

  const details = report.channels.length === 0 ? '' : `
<section>
  <h2>Statistics per channel</h2>
${report.channels.map((c) => `  <h3>CH${c.channelId} <span class="muted">· ${c.numTests} test${c.numTests !== 1 ? 's' : ''}</span></h3>
  <div class="scroll"><table>
    <thead><tr><th>Metric</th><th class="num">n</th><th class="num">Mean</th><th class="num">SD</th><th class="num">CV (%)</th><th class="num">Min</th><th class="num">Max</th></tr></thead>
    <tbody>
${REPORT_METRICS.map((m) => {
    const s = c.distributions[m.key];
    const empty = count(s) === 0;
    const v = (x: number | null) => (empty ? '—' : formatMetricValue(x));
    return `      <tr><th scope="row">${metricHeader(m)}</th><td class="num">${count(s)}</td><td class="num">${v(s.mean)}</td><td class="num">${v(s.stdDev)}</td><td class="num">${v(coefficientOfVariation(s))}</td><td class="num">${v(s.min)}</td><td class="num">${v(s.max)}</td></tr>`;
  }).join('\n')}
    </tbody>
  </table></div>`).join('\n')}
</section>`;

  const perTest = report.channels.length === 0 ? '' : `
<section>
  <h2>Values per measurement</h2>
  <p class="hint">— means the metric could not be calculated for that test, for example because it has no specimen sweep.</p>
${report.channels.map((c) => {
    const rows = report.tests
      .map((t) => ({ test: t, values: t.channels.find((v) => v.channelId === c.channelId) }))
      .filter((r): r is { test: typeof r.test; values: ChannelMetricValues } => r.values !== undefined);
    return `  <h3>CH${c.channelId}</h3>
  <div class="scroll"><table class="dense">
    <thead><tr><th>Test</th><th>ID</th><th>Status</th>${REPORT_METRICS.map((m) => `<th class="num">${metricHeader(m)}</th>`).join('')}</tr></thead>
    <tbody>
${rows.map(({ test, values }) => `      <tr><th scope="row">${escapeHtml(formatTestName(test))}</th><td class="id" title="${escapeHtml(test.testId)}">${escapeHtml(test.testId.slice(0, 8))}</td><td>${escapeHtml(formatTestStatus(test.status))}</td>${REPORT_METRICS.map((m) => `<td class="num">${formatMetricValue(values[m.key])}</td>`).join('')}</tr>`).join('\n')}
    </tbody>
  </table></div>`;
  }).join('\n')}
</section>`;

  const excluded = report.excludedTests.length === 0 ? '' : `
<section>
  <h2>Tests left out</h2>
  <div class="scroll"><table>
    <thead><tr><th>Test</th><th>ID</th><th>Reason</th></tr></thead>
    <tbody>
${report.excludedTests.map((t) => `      <tr><th scope="row">${escapeHtml(formatTestName(t))}</th><td class="id" title="${escapeHtml(t.testId)}">${escapeHtml(t.testId.slice(0, 8))}</td><td>${escapeHtml(describeExclusion(t))}</td></tr>`).join('\n')}
    </tbody>
  </table></div>
</section>`;

  const empty = report.numTestsAnalyzed === 0
    ? '<p class="empty">None of the selected tests had usable data, so there are no metrics to report.</p>'
    : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Calibration metrics report — ${escapeHtml(generatedAt.toLocaleString())}</title>
<style>
  :root { color-scheme: light; --ink: #1d2433; --muted: #5f6b7a; --line: #d9dee5; --head: #f3f5f8; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 24px 16px 48px; background: #fff; color: var(--ink);
         font: 14px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  main { max-width: 1200px; margin: 0 auto; }
  h1 { font-size: 22px; margin: 0 0 12px; }
  h2 { font-size: 17px; margin: 32px 0 6px; padding-bottom: 4px; border-bottom: 1px solid var(--line); }
  h3 { font-size: 14px; margin: 20px 0 6px; }
  dl.meta { display: grid; grid-template-columns: max-content 1fr; gap: 2px 16px; margin: 0; }
  dl.meta dt { color: var(--muted); }
  dl.meta dd { margin: 0; }
  .hint, .muted, .unit { color: var(--muted); }
  .hint { margin: 0 0 8px; font-size: 13px; }
  .unit { font-weight: normal; }
  .empty { margin-top: 24px; padding: 12px; border: 1px solid var(--line); border-radius: 6px; }
  .scroll { overflow-x: auto; }
  table { border-collapse: collapse; width: 100%; }
  th, td { padding: 5px 8px; border-bottom: 1px solid var(--line); text-align: left; vertical-align: top; }
  thead th { background: var(--head); font-weight: 600; white-space: nowrap; }
  tbody th { font-weight: 500; white-space: nowrap; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .id { font-family: ui-monospace, Consolas, monospace; font-size: 12px; color: var(--muted); }
  table.dense { font-size: 12px; }
  footer { margin-top: 40px; color: var(--muted); font-size: 12px; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print {
    body { padding: 0; font-size: 11px; }
    .scroll { overflow: visible; }
    tr { break-inside: avoid; }
    h2, h3 { break-after: avoid; }
  }
</style>
</head>
<body>
<main>
<header>
  <h1>Calibration metrics report</h1>
  <dl class="meta">
    <dt>Generated</dt><dd>${escapeHtml(generatedAt.toLocaleString())}</dd>
    <dt>Generated by</dt><dd>${escapeHtml(context.generatedBy)}</dd>
    <dt>Tests</dt><dd>${report.numTestsAnalyzed} of ${report.numTestsRequested} selected tests analyzed${report.excludedTests.length > 0 ? `, ${report.excludedTests.length} left out` : ''}</dd>
    <dt>Channels</dt><dd>${channelNames.length > 0 ? channelNames.join(', ') : '—'}</dd>
  </dl>
</header>
${empty}${summary}${details}${perTest}${excluded}
<footer>Ediphor LabCloud · Metrics are calculated from each test's stored baseline and specimen sweeps; Δ metrics compare the specimen sweep with the baseline. This report contains metrics only, no threshold recommendations.</footer>
</main>
</body>
</html>
`;
}
